import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { normalizeAgentEmployeeList } from "@sd/lib/adminTicketUtils";
import { mergeRequesterFilterOptions } from "@sd/lib/ticketRequesterFilter";
import {
  buildPriorityIdToLabelMap,
  parsePriorityListingOptions,
  type PriorityListingOption,
} from "@sd/lib/priorityListingUtils";
import { buildCombinedRequesterDisplayMap } from "@sd/lib/userDisplayMap";
import ServiceDeskAPIService, {
  SERVICE_DESK_FORMILY_CUSTOMER_COMPANY_MODEL,
  agentEmployeeListQueryKey,
  serviceDeskEmployeeListQueryKey,
  serviceDeskPriorityListingQueryKey,
} from "@sd/services/APIs";

type UseServiceDeskUserDisplayMapOptions = {
  /** Agent / Super Agent panel — refetch lookup APIs on each mount (admin parity). */
  refetchAgentListsOnMount?: boolean;
};

/**
 * Requester / assignee display names from service-desk sources:
 * - GET /service_desk/agent_employee_list/
 * - GET /service_desk/employee_list/
 * - GET /service_desk/priority_listing/
 * - ServiceDesk Enduser grid_data (Formily)
 * - ServiceDesk Customer Company grid_data (Formily)
 */
export function useServiceDeskUserDisplayMap(
  options?: UseServiceDeskUserDisplayMapOptions
): {
  userDisplayMap: Map<string, string>;
  agentEmployees: { id: string; name: string }[];
  agentEmployeePayload: unknown;
  employeeListPayload: unknown;
  priorityListingPayload: unknown;
  priorityOptions: PriorityListingOption[];
  priorityIdToLabel: Map<string, string>;
  requesterFilterOptions: { id: string; name: string }[];
} {
  const refetchOnMount = options?.refetchAgentListsOnMount ? "always" : undefined;

  const { data: agentEmployeePayload } = useQuery({
    queryKey: agentEmployeeListQueryKey,
    queryFn: async () => {
      try {
        const res = await ServiceDeskAPIService.lists.agentEmployees();
        return res.data as unknown;
      } catch {
        return undefined;
      }
    },
    staleTime: 60_000,
    refetchOnMount,
  });

  const { data: employeeListPayload } = useQuery({
    queryKey: serviceDeskEmployeeListQueryKey,
    queryFn: async () => {
      try {
        const res = await ServiceDeskAPIService.lists.employees();
        return res.data as unknown;
      } catch {
        return undefined;
      }
    },
    staleTime: 60_000,
    refetchOnMount,
  });

  const { data: priorityListingPayload } = useQuery({
    queryKey: serviceDeskPriorityListingQueryKey,
    queryFn: async () => {
      try {
        const res = await ServiceDeskAPIService.priorities.list();
        return res.data as unknown;
      } catch {
        return undefined;
      }
    },
    staleTime: 60_000,
    refetchOnMount,
  });

  const { data: enduserPayload } = useQuery({
    queryKey: ["sd-enduser-list"],
    queryFn: async () => {
      try {
        const res = await ServiceDeskAPIService.lists.enduserGrid();
        return res.data as unknown;
      } catch {
        return undefined;
      }
    },
    staleTime: 60_000,
    retry: 1,
  });

  const { data: customerCompanyPayload } = useQuery({
    queryKey: ["sd-customer-company-grid"],
    queryFn: async () => {
      try {
        const res = await ServiceDeskAPIService.lists.customerCompanyGrid(
          SERVICE_DESK_FORMILY_CUSTOMER_COMPANY_MODEL
        );
        return res.data as unknown;
      } catch {
        return undefined;
      }
    },
    staleTime: 60_000,
    retry: 1,
  });

  const agentEmployees = useMemo(
    () => normalizeAgentEmployeeList(agentEmployeePayload),
    [agentEmployeePayload]
  );

  const priorityOptions = useMemo(
    () => parsePriorityListingOptions(priorityListingPayload),
    [priorityListingPayload]
  );

  const priorityIdToLabel = useMemo(
    () => buildPriorityIdToLabelMap(priorityOptions),
    [priorityOptions]
  );

  const requesterFilterOptions = useMemo(
    () =>
      mergeRequesterFilterOptions(agentEmployeePayload, employeeListPayload),
    [agentEmployeePayload, employeeListPayload]
  );

  return useMemo(
    () => ({
      userDisplayMap: buildCombinedRequesterDisplayMap(
        agentEmployeePayload,
        employeeListPayload,
        enduserPayload,
        customerCompanyPayload
      ),
      agentEmployees,
      agentEmployeePayload,
      employeeListPayload,
      priorityListingPayload,
      priorityOptions,
      priorityIdToLabel,
      requesterFilterOptions,
    }),
    [
      agentEmployees,
      agentEmployeePayload,
      employeeListPayload,
      enduserPayload,
      customerCompanyPayload,
      priorityListingPayload,
      priorityOptions,
      priorityIdToLabel,
      requesterFilterOptions,
    ]
  );
}
